
import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Search, X, Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface KolOption {
  id: string;
  value: string;
  label: string;
  creator_name: string;
  social_media_handler: string;
  profile_image_url?: string;
}

interface TwitterKolSelectorProps {
  selectedKols: string[];
  onChange: (kolUsernames: string[]) => void;
}

const TwitterKolSelector: React.FC<TwitterKolSelectorProps> = ({ selectedKols, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<KolOption[]>([]);
  const [showWarning, setShowWarning] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Show warning when more than 10 KOLs are selected
  useEffect(() => {
    setShowWarning(selectedKols.length > 10);
  }, [selectedKols]);

  // Fetch Twitter KOLs from kol_db
  const { data: twitterKols, isLoading } = useQuery({
    queryKey: ['twitter-kols'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('kol_db')
        .select('id, creator_name, social_media_handler, social_media_type, profile_image_url')
        .eq('social_media_type', 'Twitter')
        .order('creator_name');
      
      if (error) throw error;
      return data || [];
    }
  });

  // Process KOLs into options and handle filtering
  useEffect(() => {
    if (!twitterKols) {
      setFilteredOptions([]);
      return;
    }

    const kolOptions: KolOption[] = twitterKols
      .filter(kol => kol.social_media_handler)
      .map(kol => ({
        id: kol.id,
        value: kol.social_media_handler.replace('@', ''),
        label: `${kol.creator_name} (@${kol.social_media_handler.replace('@', '')})`,
        creator_name: kol.creator_name,
        social_media_handler: kol.social_media_handler.replace('@', ''),
        profile_image_url: kol.profile_image_url
      }));

    // Apply precise filtering and limit to 8 items when no search
    if (!searchTerm.trim()) {
      setFilteredOptions(kolOptions.slice(0, 8));
    } else {
      const searchLower = searchTerm.toLowerCase().trim();
      const filtered = kolOptions.filter(kol => {
        const nameMatch = kol.creator_name.toLowerCase().startsWith(searchLower);
        const usernameMatch = kol.social_media_handler.toLowerCase().startsWith(searchLower);
        const exactNameMatch = kol.creator_name.toLowerCase() === searchLower;
        const exactUsernameMatch = kol.social_media_handler.toLowerCase() === searchLower;
        
        return exactNameMatch || exactUsernameMatch || nameMatch || usernameMatch;
      });
      setFilteredOptions(filtered);
    }
  }, [twitterKols, searchTerm]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleSelection = (kolUsername: string) => {
    const newSelection = selectedKols.includes(kolUsername)
      ? selectedKols.filter(k => k !== kolUsername)
      : [...selectedKols, kolUsername];
    
    onChange(newSelection);
  };

  const handleRemoveKol = (kolUsername: string) => {
    onChange(selectedKols.filter(k => k !== kolUsername));
  };

  const getSelectedKolData = (username: string) => {
    return filteredOptions.find(kol => kol.value === username) || 
           twitterKols?.find(kol => kol.social_media_handler.replace('@', '') === username);
  };

  const getKolInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Twitter KOLs</label>
        <span className="text-xs text-muted-foreground">{selectedKols.length} selected</span>
      </div>
      
      {/* Selected KOLs Display */}
      {selectedKols.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-md border">
          {selectedKols.map((username) => {
            const kolData = getSelectedKolData(username);
            return (
              <Badge
                key={username}
                variant="outline"
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-100 flex items-center gap-2 px-2 py-1"
              >
                <Avatar className="h-4 w-4">
                  <AvatarImage 
                    src={kolData?.profile_image_url} 
                    alt={kolData?.creator_name || username}
                  />
                  <AvatarFallback className="text-xs bg-gray-200 text-gray-600">
                    {getKolInitials(kolData?.creator_name || username)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs">
                  {kolData?.creator_name || username}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-gray-200"
                  onClick={() => handleRemoveKol(username)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            );
          })}
        </div>
      )}

      {/* Dropdown Trigger */}
      <div className="relative" ref={dropdownRef}>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-between text-left font-normal"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) {
              setTimeout(() => inputRef.current?.focus(), 100);
            }
          }}
        >
          <span className="text-muted-foreground">
            {selectedKols.length === 0 
              ? "Select Twitter KOLs to filter" 
              : `${selectedKols.length} KOL${selectedKols.length > 1 ? 's' : ''} selected`
            }
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>

        {/* Dropdown Content */}
        {isOpen && (
          <div className="absolute top-full mt-1 w-full bg-white border rounded-md shadow-lg z-[9999] overflow-hidden">
            {/* Search Input */}
            <div className="p-3 border-b bg-white">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  ref={inputRef}
                  placeholder="Search KOLs by name or username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Options List with proper scrolling */}
            <div className="max-h-64 overflow-y-auto bg-white">
              <ScrollArea className="h-full">
                <div className="p-1">
                  {isLoading ? (
                    <div className="p-3 text-center text-sm text-muted-foreground">
                      Loading KOLs...
                    </div>
                  ) : filteredOptions.length === 0 ? (
                    <div className="p-3 text-center text-sm text-muted-foreground">
                      {searchTerm ? 'No KOLs found matching your search' : 'No Twitter KOLs available'}
                    </div>
                  ) : (
                    filteredOptions.map((kol) => (
                      <div
                        key={kol.id}
                        className={cn(
                          "flex items-center gap-3 p-2 cursor-pointer rounded-sm transition-colors",
                          selectedKols.includes(kol.value) 
                            ? "bg-blue-50 hover:bg-blue-100" 
                            : "hover:bg-gray-50"
                        )}
                        onClick={() => handleToggleSelection(kol.value)}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage 
                            src={kol.profile_image_url} 
                            alt={kol.creator_name}
                          />
                          <AvatarFallback className="text-xs bg-gray-200 text-gray-600">
                            {getKolInitials(kol.creator_name)}
                          </AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 truncate">
                            {kol.creator_name}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            @{kol.social_media_handler}
                          </div>
                        </div>
                        
                        {selectedKols.includes(kol.value) && (
                          <Check className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        )}
      </div>
      
      {showWarning && (
        <Alert variant="warning" className="py-2">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle className="text-xs font-medium">High KOL Count Warning</AlertTitle>
          <AlertDescription className="text-xs">
            Processing many KOLs may trigger Twitter API rate limits. Consider using background mode for large selections.
          </AlertDescription>
        </Alert>
      )}
      
      <p className="text-xs text-muted-foreground">
        Filter tweets by specific Twitter KOLs. If none selected, all tweets matching the query will be fetched.
        <span className="text-yellow-500"> Note: KOLs are processed in batches of 10 to avoid rate limiting.</span>
      </p>
    </div>
  );
};

export default TwitterKolSelector;
